import {Trans} from '@lingui/macro';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {IconButton} from '../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppSelector} from '../../../../helpers/reduxHelpers';
import {LocalApplicationModel} from '../../../../models/api/localApplicationModel';
import {getCompany} from '../../../../store/data/company/companySelectors';
import {fetchOauthClientSecretAsync} from '../../../fetchers/oauthClient';
import {AtomLine} from '../../../rules/ruleEditor/expressions/atomLine';
import {
  StyledParameterLineDiv,
  StyledParameterTitleLineDiv,
} from '../../../rules/ruleEditor/expressions/parameterLine';
import {RuleMultilineParameterCard} from '../../../rules/ruleEditor/ruleMultilineParameterCard';

/*
 * Constants.
 */

const censoredClientSecret = '*'.repeat(40);

/*
 * Types.
 */

interface OauthClientCredentialsCardProps {
  application: LocalApplicationModel | undefined;
  clientId: string | undefined;
}

/*
 * Styles.
 */

const StyledParameterTitleLineWithIconDiv = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  gap: 8px;
`;

/*
 * Component.
 */

export const OauthClientCredentialsCard: FC<OauthClientCredentialsCardProps> = (props) => {
  const {application, clientId} = props;

  const [displayedClientSecret, setDisplayedClientSecret] = useState(censoredClientSecret);
  const [clientSecret, setClientSecret] = useState('');
  const company = useAppSelector(getCompany);

  const onToggleClientSecret = async () => {
    if (clientSecret) {
      setDisplayedClientSecret(
        displayedClientSecret === censoredClientSecret ? clientSecret : censoredClientSecret,
      );
    } else {
      const response = await fetchOauthClientSecretAsync(company, application);
      if (!response) {
        return;
      }
      setClientSecret(response.clientSecret);
      setDisplayedClientSecret(response.clientSecret);
    }
  };

  const title = (
    <AtomLine>
      <span>
        <Trans id="oauth-client-credentials-title">Client credentials</Trans>
      </span>
      {!clientId && renderClientCredentialsTooltip()}
    </AtomLine>
  );

  return (
    <RuleMultilineParameterCard title={title} icon={<Icon name="lockFilled" />}>
      {clientId && renderCredentials(clientId, displayedClientSecret, onToggleClientSecret)}
    </RuleMultilineParameterCard>
  );
};

/*
 * Helpers.
 */

function renderClientCredentialsTooltip() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <TooltipCoordinator
          iconColor={theme.greys.shade70}
          render={() => (
            <Trans id="oauth-client-credentials-tooltip">
              Credentials will be accessible after creating the OAuth client.
            </Trans>
          )}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderCredentials(clientId: string, clientSecret: string, onToggleClientSecret: Handler) {
  return (
    <>
      <StyledParameterLineDiv>
        <StyledParameterTitleLineDiv>
          <Trans id="oauth-client-client-id">Client ID</Trans>
        </StyledParameterTitleLineDiv>
        <TextBox value={clientId} size={VisualSizesEnum.LARGE} noMargin isDisabled />
      </StyledParameterLineDiv>

      <StyledParameterLineDiv>
        <StyledParameterTitleLineWithIconDiv>
          <Trans id="oauth-client-client-secret">Client secret</Trans>
          <IconButton
            label={<Trans id="oauth-client-toggle-client-secret">Toggle client secret</Trans>}
            iconName="eyeCrossedFilledSmall"
            onClick={onToggleClientSecret}
          />
        </StyledParameterTitleLineWithIconDiv>
        <TextBox value={clientSecret} size={VisualSizesEnum.LARGE} noMargin isDisabled />
      </StyledParameterLineDiv>
    </>
  );
}
